import { Field } from './Field';
import { MediaField } from './MediaField';
import { NumberInput } from './NumberInput';
import { isMediaShape } from '../../core/rules/media';
import { fieldLabels, humanize } from '../adminLabels';
import { ContentTextField } from './ContentTextField';
import { ContentListField } from './ContentListField';
import { isObject, tint, type FieldOverrides, type Json } from './contentShape';
export type { FieldOverrides, Json } from './contentShape';
type Props = {
  value: Json;
  onChange: (value: Json) => void;
  fieldKey?: string;
  depth?: number;
  /** Labels per veld voor dit blok; gelden alleen voor de velden direct in het blok. */
  labels?: FieldOverrides;
  /** Label en uitleg van dit veld zelf (doorgegeven vanuit het blok). */
  override?: {
    label: string;
    hint?: string;
  };
};

/**
 * Bouwt automatisch een formulier op uit de vorm van de data. Zo is elke tekst op de site te
 * bewerken zonder dat er per blok een scherm gebouwd hoeft te worden, en een nieuw tekstveld in
 * data/pages.ts verschijnt hier vanzelf. Tekst: `ContentTextField`; lijsten: `ContentListField`;
 * geneste groepen als fieldset met een zachte vlakkleur per laag.
 */
export function ContentEditor({
  value,
  onChange,
  fieldKey,
  depth = 0,
  labels,
  override
}: Props) {
  const base = fieldKey ? fieldLabels[fieldKey] : undefined;
  if (base?.hidden) return null;
  const meta = override ? {
    ...base,
    label: override.label,
    hint: override.hint ?? base?.hint
  } : base;
  const label = override?.label ?? (fieldKey ? humanize(fieldKey) : '');
  if (typeof value === 'string') {
    return <ContentTextField fieldKey={fieldKey ?? ''} label={label} hint={meta?.hint} value={value} onChange={onChange} data-id="element-4931" data-mp="3bc135nmux0ex" />;
  }
  if (typeof value === 'number') {
    return <Field label={label} hint={meta?.hint} data-id="element-4932" data-mp="3l3p0u6a0bdou">
        <NumberInput decimals value={Number.isFinite(value) ? value : 0} onChange={v => v !== undefined && onChange(v)} className="max-w-[12rem]" data-id="element-4933" data-mp="3q3jqy7lyjz8u" />
      </Field>;
  }
  if (typeof value === 'boolean') {
    return <label className="flex min-h-11 items-center gap-3 text-[15px] text-ink sm:min-h-0 sm:gap-2 sm:text-[14px]" data-id="element-4934" data-mp="35dfw1zgw8q8n">
        <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="h-5 w-5 shrink-0 accent-clay sm:h-4 sm:w-4" data-id="element-4935" data-mp="30gh0rdlhud8v" />
        {label}
      </label>;
  }
  if (Array.isArray(value)) {
    return <ContentListField fieldKey={fieldKey ?? ''} label={label} value={value} onChange={onChange} depth={depth} data-id="element-4936" data-mp="3ac0vvxs8h7wk" />;
  }

  // Een beeld ({ image, alt, focus?, video? }): één veld met uitsnede en video, niet vier losse.
  if (isMediaShape(value)) {
    return <MediaField label={label || 'Beeld'} hint={meta?.hint} value={value} onChange={next => onChange(next as unknown as Json)} data-id="element-4937" data-mp="1y1xkphshafw" />;
  }
  if (isObject(value)) {
    const entries = Object.entries(value).filter(([k]) => !fieldLabels[k]?.hidden);
    const body = <div className="space-y-5" data-id="element-4938" data-mp="23qq69ed7cq32">
        {entries.map(([k, v]) => <ContentEditor key={k} fieldKey={k} value={v} depth={depth + 1} override={labels?.[k]} onChange={next => onChange({
        ...value,
        [k]: next
      })} data-id="element-4939" data-mp="1yrdau589maov" />)}
      </div>;
    if (!fieldKey || depth === 0) return body;
    return <fieldset className={`rounded-md p-4 ${tint(depth)}`} data-id="element-4940" data-mp="4hyftlse5fwk">
        <legend className="px-1.5 text-[13px] font-medium text-ink" data-id="element-4941" data-mp="4hokrjshoik0">{label}</legend>
        {meta?.hint && <p className="-mt-1 mb-3 text-[12px] text-muted" data-id="element-4942" data-mp="9f6x7k5ptfwr">{meta.hint}</p>}
        {body}
      </fieldset>;
  }
  return null;
}